export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  duration: number;
}

type Listener = () => void;

const MAX_VISIBLE = 4;
const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<Listener>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): ToastItem[] {
  return toasts;
}

export function addToast(
  variant: ToastVariant,
  message: string,
  opts?: { duration?: number },
): number {
  const id = nextId++;
  const duration =
    opts?.duration ?? (variant === 'error' ? ERROR_DURATION : DEFAULT_DURATION);
  toasts = [{ id, variant, message, duration }, ...toasts].slice(0, MAX_VISIBLE);
  emit();
  if (duration > 0) {
    timers.set(id, setTimeout(() => dismissToast(id), duration));
  }
  return id;
}

export function dismissToast(id: number): void {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  const next = toasts.filter((t) => t.id !== id);
  if (next.length !== toasts.length) {
    toasts = next;
    emit();
  }
}

/** Test-only reset. */
export function __resetToasts(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  listeners.clear();
  toasts = [];
  nextId = 1;
}
